import React, { useEffect, useState, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { ConfigContext } from "../../context/GlobalContext";
import { FilterdData } from "../../helper/FilterdData";

const ProfitChart = ({ InvoicesData }) => {
  const { state } = useContext(ConfigContext);
  const [filterdInvoiceData, setFilterdInvoiceData] = useState(InvoicesData);

  // Update filtered data whenever state or ACTIVEFILTER changes
  // useEffect(() => {
  //   setFilterdInvoiceData(FilterdData(state, InvoicesData, "date"));
  // }, [state, InvoicesData]);

  // Helper: Filter data according to ACTIVEFILTER
  const filterByActive = (data, dateField) => {
    const now = new Date();
    return data.filter((item) => {
      const itemDate = new Date(Number(item[dateField] || item.date || item.createdAt || item.createdDate));
      switch (state?.ACTIVEFILTER) {
        case "Today":
          return itemDate.toDateString() === now.toDateString();
        case "Yeasterday":
          const yesterday = new Date();
          yesterday.setDate(now.getDate() - 1);
          return itemDate.toDateString() === yesterday.toDateString();
        case "Last 7 Days":
          const last7 = new Date();
          last7.setDate(now.getDate() - 7);
          return itemDate >= last7 && itemDate <= now;
        case "This Month":
          return (
            itemDate.getMonth() === now.getMonth() &&
            itemDate.getFullYear() === now.getFullYear()
          );
        case "Last Month":
          const lastMonth = new Date();
          lastMonth.setMonth(now.getMonth() - 1);
          return (
            itemDate.getMonth() === lastMonth.getMonth() &&
            itemDate.getFullYear() === lastMonth.getFullYear()
          );
        case "Custom":
          const { startDate, endDate } = state.CUSTOM_RANGE || {};
          if (!startDate || !endDate) return true;
          return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
        default:
          return true;
      }
    });
  };
  const getBucketKey = (date, filter) => {
    const d = new Date(date);

    switch (filter) {
      case "Today":
      case "Yeasterday":
        return (() => {
          // If your timestamp is in seconds, multiply by 1000
          const dateObj = new Date(Number(date)); // or *1000 if seconds
          let hours = dateObj.getHours(); // 0-23
          const ampm = hours >= 12 ? "PM" : "AM";

          // Convert 24h to 12h format
          hours = hours % 12;
          hours = hours === 0 ? 12 : hours; // 0 => 12

          // Pad hours to 2 digits if needed
          const paddedHour = hours.toString().padStart(2, "0");

          return `${paddedHour}:00 ${ampm}`;
        })();


      case "Last 7 Days":
        // Day format (e.g. 28 Sep)
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });

      case "This Month":
      case "Last Month":
        // Week format (e.g. "Week 1", "Week 2")
        const weekNum = Math.ceil(d.getDate() / 7);
        return `Week ${weekNum}`;

      case "Custom":
        // Default to daily
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });

      case "All":
      default:
        // Month-wise format (e.g. "Sep 2025")
        return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
  };
  const filteredInvoices = filterByActive(InvoicesData, "date");
  const filteredExpenses = filterByActive(state?.EXPENSE || [], "createdDate");

  // Group revenue by date
  const revenueByDate = filteredInvoices.reduce((acc, invoice) => {
    const rawDate = Number(invoice.date);
    const key = getBucketKey(rawDate, state?.ACTIVEFILTER); // Label
    if (!acc[key]) acc[key] = { total: 0, raw: rawDate };
    acc[key].total += Number(invoice.totalAmount || 0);
    return acc;
  }, {});

  // Group expense
  const expenseByDate = filteredExpenses.reduce((acc, exp) => {
    const rawDate = Number(exp.createdDate || exp.createdAt);
    const key = getBucketKey(rawDate, state?.ACTIVEFILTER); // Label
    if (!acc[key]) acc[key] = { total: 0, raw: rawDate };
    acc[key].total += Number(exp.amount || 0);
    return acc;
  }, {});

  // ✅ Sort by raw date not label
  const categories = Array.from(
    new Set([...Object.keys(revenueByDate), ...Object.keys(expenseByDate)])
  );


  // Series data
  const revenueSeries = categories.map((label) => revenueByDate[label]?.total || 0);
  const expenseSeries = categories.map((label) => expenseByDate[label]?.total || 0);
  const profitSeries = categories.map(
    (label, i) => revenueSeries[i] - expenseSeries[i]
  );


  const chartOptions = {
    chart: {
      type: "area",
      height: 400,
      toolbar: { show: false },
    },

    grid: {
      padding: {
        left: 60, // ✅ keep balanced (NOT 200)
        right: 10,
      },
    },

    xaxis: {
      categories,
      title: { text: "Date" },
    },

    yaxis: {
      title: { text: "Amount ($)" },

      labels: {
        minWidth: 80,
        maxWidth: 100,
        offsetX: 10, // ✅ small push only
        formatter: function (val) {
          if (val >= 1e9) return (val / 1e9).toFixed(2) + "B";
          if (val >= 1e6) return (val / 1e6).toFixed(2) + "M";
          if (val >= 1e3) return (val / 1e3).toFixed(2) + "K";
          return val.toFixed(2); // ✅ FIXED 2 decimal
        },
      },

      axisBorder: {
        show: true,
        offsetX: 0, // ❌ don’t shift axis too much
      },

      axisTicks: {
        show: true,
        offsetX: 0,
      },
    },

    stroke: { curve: "smooth" },
    dataLabels: { enabled: false },
  };

  const chartSeries = [
    { name: "Revenue", data: revenueSeries },
    { name: "Expense", data: expenseSeries },
    { name: "Profit/Loss", data: profitSeries },
  ];

  // Total profit/loss
  const totalRevenue = revenueSeries.reduce((sum, val) => sum + val, 0);
  const totalExpense = expenseSeries.reduce((sum, val) => sum + val, 0);
  const total = totalRevenue - totalExpense;
  const isProfit = total >= 0;
  const profitAmount = isProfit ? total : 0;
  const lossAmount = !isProfit ? Math.abs(total) : 0;

  const profitPercentage =
    totalRevenue > totalExpense
      ? ((totalRevenue - totalExpense) / totalRevenue) * 100
      : 0;
  const lossPercentage =
    totalExpense > totalRevenue
      ? ((totalExpense - totalRevenue) / totalExpense) * 100
      : 0;

  return (
    <div className="col-12">
      <div className="card h-100">
        <div className="card-body p-24 mb-20">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg mb-0">Revenue Statistic</h6>
          </div>

          {/* Profit & Loss display */}
          <ul className="d-flex flex-wrap align-items-center justify-content-center my-3 gap-24">
            <li className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <span className="w-8-px h-8-px rounded-pill bg-success" />
                <span className="text-secondary-light text-sm fw-semibold">
                  Profit
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 text-success">
                  {state?.SYMBOL === "$"
                    ? `${state?.CURRENCYCODE} ${state?.SYMBOL} `
                    : `${state?.SYMBOL} `}{profitAmount.toFixed(2)}</h6>
                <small className="text-secondary-light text-success">
                  ({profitPercentage.toFixed(2)}%)
                </small>
              </div>
            </li>

            <li className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <span className="w-8-px h-8-px rounded-pill bg-danger" />
                <span className="text-secondary-light text-sm fw-semibold">
                  Loss
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 text-danger">
                  {state?.SYMBOL === "$"
                    ? `${state?.CURRENCYCODE} ${state?.SYMBOL} `
                    : `${state?.SYMBOL} `}
                  {lossAmount.toFixed(2)}</h6>
                <small className="text-secondary-light text-danger">
                  ({lossPercentage.toFixed(2)}%)
                </small>
              </div>
            </li>
          </ul>

          {/* ApexChart */}
          <div id="revenueChart" className="apexcharts-tooltip-style-1">
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="area"
              height={400}
              width="100%"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitChart;
